// Lifecycle policy for Event Ideas — mirrors `submission-policy.ts` in shape.
//
// Pure, no Vue/Nuxt imports (auto-imported by Nuxt from `app/utils/`). Ideas
// have a much flatter lifecycle than submissions: `active` is the only state in
// which anything can be done to an idea, and everything else is terminal from
// the author's point of view.

import type { EventIdeaResource, IdeaStatus } from '@@/types/api';

/**
 * An author may hold at most 5 `active` ideas at once (backend [S4]). Hitting
 * the cap aborts with a bare `{ message }` 422 — NOT a validation-error bag —
 * so the UI surfaces it as a page-level alert, never a field error.
 */
export const MAX_ACTIVE_IDEAS = 5;

// ---------------------------------------------------------------------------
// Status badge — Vuetify color token + mdi icon + human label per status
// ---------------------------------------------------------------------------

interface IdeaStatusBadge {
  color: string;
  icon: string;
  label: string;
}

const statusMap: Record<IdeaStatus, IdeaStatusBadge> = {
  active: {
    color: 'success',
    icon: 'mdi-lightbulb-on-outline',
    label: 'Active',
  },
  superseded: {
    color: 'info',
    icon: 'mdi-rocket-launch-outline',
    label: 'Promoted',
  },
  withdrawn: {
    color: 'grey',
    icon: 'mdi-archive-outline',
    label: 'Withdrawn',
  },
  removed: {
    color: 'error',
    icon: 'mdi-close-circle',
    label: 'Removed',
  },
};

const fallbackBadge: IdeaStatusBadge = {
  color: 'surface-variant',
  icon: 'mdi-help-circle',
  label: 'Unknown',
};

export function ideaStatusBadge(status: IdeaStatus): IdeaStatusBadge {
  return statusMap[status] ?? fallbackBadge;
}

// ---------------------------------------------------------------------------
// Lifecycle capability checks
// ---------------------------------------------------------------------------

/** Only `active` ideas are editable (backend PATCH 422s on anything else). */
export function canEditIdea(status: IdeaStatus): boolean {
  return status === 'active';
}

/** Only `active` ideas can be withdrawn (the withdraw endpoint 422s otherwise). */
export function canWithdrawIdea(status: IdeaStatus): boolean {
  return status === 'active';
}

/**
 * Promotion (seeding a draft event from the idea) is the AUTHOR's action on
 * their own `active` idea. Takes the resource rather than the bare status
 * because the caller almost always pairs it with `isDraftingIdea` below.
 *
 * Deliberately true whether or not a draft already exists: `promote` is
 * idempotent on the backend, and the "already drafting" case swaps the button
 * to a plain link rather than hiding the affordance. `isOwn` defaults to the
 * resource's own flag; pass it explicitly when a fresher signal is available.
 */
export function canPromoteIdea(
  idea: EventIdeaResource,
  isOwn: boolean = idea.is_own,
): boolean {
  return idea.status === 'active' && isOwn;
}

/**
 * True while a draft event has been seeded from this idea but the idea itself
 * is still live. Picks the "Continue draft" variant (a direct link to the
 * draft, NO API call) over "Draft event", and drives the dashboard's
 * "Drafting" chip.
 *
 * Orthogonal to `IdeaStatus`: once the spawned event reaches fundraising the
 * backend flips the idea to `superseded`, at which point this goes false and
 * the detail page's 301 takes over.
 */
export function isDraftingIdea(idea: EventIdeaResource): boolean {
  return idea.status === 'active' && idea.spawned_event_id !== null;
}

/**
 * Reporting is for OTHER people's live ideas. `isOwn` defaults to the
 * resource's own flag; pass it explicitly when a fresher signal is available
 * (e.g. the viewer was resolved client-side after an SSR visitor render).
 */
export function canReportIdea(
  idea: EventIdeaResource,
  isOwn: boolean = idea.is_own,
): boolean {
  return idea.status === 'active' && !isOwn;
}

/** True once the author holds `MAX_ACTIVE_IDEAS` (or more) `active` ideas. */
export function atActiveIdeaCap(ideas: EventIdeaResource[]): boolean {
  return countActiveIdeas(ideas) >= MAX_ACTIVE_IDEAS;
}

/** How many of the supplied ideas count against the active cap. */
export function countActiveIdeas(ideas: EventIdeaResource[]): number {
  return ideas.filter((idea) => idea.status === 'active').length;
}
